import React, {useEffect, useState} from 'react'
import {Box} from '@mui/material'
import SelectNew from 'src/components-new/inputs/SelectNew'
import TextFieldNew from 'src/components-new/inputs/TextFieldNew'
import {STATUS} from 'src/constants/constants'
import apiClient from 'src/services/apiClient'
import {AddressValue} from '../selfRegistrationValidation'
import {Option} from '../dayTimeOptions'

interface AddressFieldsProps {
  value: AddressValue
  onChange: (value: AddressValue) => void
  errors: Record<string, string>
  disabled?: boolean
}

const GRID_SX = {
  display: 'grid',
  gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, 1fr)'},
  gap: 2,
} as const

/**
 * Controlled address subform — new-style equivalent of the "Indirizzo" card in
 * src/pages/selfRegistration/steps/SecondStep/SecondStep.tsx:274-372. `country` is fixed
 * to "Italia" (read-only), matching the legacy `form.setFieldValue('country', 'Italia')`.
 *
 * Simplification vs. legacy: instead of re-fetching provinces/cities on every keystroke
 * (no debounce, an unfiltered fetch also firing on mount), each level is fetched ONCE when
 * its parent level changes and `SelectNew`'s `showSearch` Autocomplete filters that
 * already-fetched list client-side — region/province/city lists are small enough once
 * scoped to a parent selection that a live server search isn't needed.
 */
export default function AddressFields({value, onChange, errors, disabled}: AddressFieldsProps): JSX.Element {
  const [regionOptions, setRegionOptions] = useState<Option[]>([])
  const [provinceOptions, setProvinceOptions] = useState<Option[]>([])
  const [cityOptions, setCityOptions] = useState<Option[]>([])
  const [regionsStatus, setRegionsStatus] = useState<string>(STATUS.PENDING)
  const [provincesStatus, setProvincesStatus] = useState<string>()
  const [citiesStatus, setCitiesStatus] = useState<string>()

  useEffect(() => {
    apiClient.getRegions('', setRegionsStatus).then((data: any) => {
      if (Array.isArray(data)) setRegionOptions(data)
    })
  }, [])

  useEffect(() => {
    if (!value.region) {
      setProvinceOptions([])
      return
    }
    apiClient.getProvinces({region: value.region}, setProvincesStatus).then((data: any) => {
      if (Array.isArray(data)) setProvinceOptions(data)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.region])

  useEffect(() => {
    if (!value.province) {
      setCityOptions([])
      return
    }
    apiClient.getCities({province: value.province}, setCitiesStatus).then((data: any) => {
      if (Array.isArray(data)) setCityOptions(data)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.province])

  const handleRegionChange = (region: string) => {
    onChange({...value, region, province: '', city: ''})
  }

  const handleProvinceChange = (province: string) => {
    onChange({...value, province, city: ''})
  }

  return (
    <Box sx={GRID_SX}>
      <TextFieldNew label="Paese" value={value.country} onChange={() => undefined} disabled fullWidth />

      <SelectNew
        label="Regione"
        value={value.region}
        onChange={handleRegionChange}
        options={regionOptions}
        showSearch
        required
        fullWidth
        disabled={disabled || regionsStatus === STATUS.PENDING}
        error={Boolean(errors.region)}
        helperText={errors.region}
      />

      <SelectNew
        label="Provincia"
        value={value.province}
        onChange={handleProvinceChange}
        options={provinceOptions}
        showSearch
        required
        fullWidth
        disabled={disabled || !value.region || provincesStatus === STATUS.PENDING}
        error={Boolean(errors.province)}
        helperText={errors.province}
      />

      <SelectNew
        label="Città"
        value={value.city}
        onChange={(city) => onChange({...value, city})}
        options={cityOptions}
        showSearch
        required
        fullWidth
        disabled={disabled || !value.province || citiesStatus === STATUS.PENDING}
        error={Boolean(errors.city)}
        helperText={errors.city}
      />

      <TextFieldNew
        label="Indirizzo"
        value={value.address}
        onChange={(address) => onChange({...value, address})}
        required
        fullWidth
        disabled={disabled}
        error={Boolean(errors.address)}
        helperText={errors.address}
      />

      <TextFieldNew
        label="Numero civico"
        value={value.streetNumber}
        onChange={(streetNumber) => onChange({...value, streetNumber})}
        required
        fullWidth
        disabled={disabled}
        error={Boolean(errors.streetNumber)}
        helperText={errors.streetNumber}
      />

      <TextFieldNew
        label="CAP"
        value={value.postalCode}
        onChange={(postalCode) => onChange({...value, postalCode})}
        required
        fullWidth
        disabled={disabled}
        error={Boolean(errors.postalCode)}
        helperText={errors.postalCode}
      />

      <TextFieldNew
        label="Località"
        value={value.locality}
        onChange={(locality) => onChange({...value, locality})}
        fullWidth
        disabled={disabled}
      />
    </Box>
  )
}
